import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Flex, Upload } from "antd";
import { IconPencilMinus, IconPencilPlus } from "@tabler/icons-react";
import api from "../../../api";
import Loader from "../../../components/spin/Loader";
import CustomHeader from "../../../components/header/CustomHeader";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import CustomEditor from "../../../components/reactquill/CustomEditor";
import CustomInput from "../../../components/input/CustomInput";
import { capitalize, formatDateTime } from "../../../utilities/utils";
import { useToast } from "../../../components/toast/toast";

const NewsDetail = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isDraftModal, setIsDraftModal] = useState(false);

  // Тус тусдаа state-ууд
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [cover, setCover] = useState([]);
  const toast = useToast();

  useEffect(() => {
    fetchNews();
  }, [id]);

  const fetchNews = async () => {
    setLoading(true);
    const res = await api.get_news_detail(id);
    // console.log("detail res", res);
    if (res.status === 200) {
      const data = res.data.data;
      setNews(data);
      setTitle(data.title || "");
      setContent(data.content || "");
      setThumbnail(data.image?.thumbnail || "");
      setCover(Array.isArray(data.image?.cover) ? data.image.cover : []);
    } else {
      toast("Мэдээ олдсонгүй", "error");
    }
    setLoading(false);
  };

  // Мэдээний төлөв солих
  const changeStatus = async (status) => {
    const res = await api.update_news_status(id, { status });
    // console.log(res);
    if (res.status === 200) {
      if (status === "draft") {
        toast("Мэдээг ноорог болголоо", "info");
      } else {
        toast("Мэдээ нийтлэгдлээ", "success");
      }
      fetchNews();
    } else {
      toast(res.data?.message || "Алдаа гарлаа", "error");
    }
  };

  const beforeUpload = (file) => {
    const isImage = file.type === "image/jpeg" || file.type === "image/png";
    const isSmall = file.size / 1024 / 1024 < 2;
    if (!isImage) toast("JPG эсвэл PNG зураг сонгоно уу", "error");
    if (!isSmall) toast("Зураг 2MB-с бага байх ёстой", "error");
    return isImage && isSmall;
  };

  // --- THUMBNAIL & БУСАД НЭГ ЗУРАГ ---
  const UploadSection = ({ label, value, onUpload }) => {
    const files = value
      ? [{ uid: "-1", name: "image.jpg", status: "done", url: value }]
      : [];

    return (
      <div style={{ marginBottom: 12 }}>
        <p style={{ marginBottom: 6 }}>{label}</p>
        <Upload
          listType="picture-card"
          maxCount={1}
          fileList={files}
          beforeUpload={beforeUpload}
          onRemove={() => onUpload("")}
          customRequest={async ({ file, onSuccess, onError }) => {
            try {
              const formData = new FormData();
              formData.append("image", file);
              const res = await api.upload_image(formData);
              const url = res.data.url;
              onUpload(url); // parent-руу буцаана
              onSuccess(res, file);
              toast("Зураг амжилттай нэмэгдлээ", "success");
            } catch (err) {
              toast("Upload амжилтгүй боллоо", "error");
              onError(err);
            }
          }}
        >
          {files.length === 0 && "+ Зураг"}
        </Upload>
      </div>
    );
  };

  // --- ОЛОН ЗУРАГТАЙ COVER GALLERY ---
  const UploadGallery = ({ value, onChange }) => {
    // props value-оос files array үүсгэнэ (state ашиглахгүй)
    const files = Array.isArray(value)
      ? value.map((url, idx) => ({
          uid: `${idx}`,
          name: `gallery-${idx}`,
          status: "done",
          url,
        }))
      : [];

    return (
      <div style={{ marginBottom: 12 }}>
        <p style={{ marginBottom: 6 }}>Cover</p>
        <Upload
          listType="picture-card"
          multiple
          fileList={files}
          beforeUpload={beforeUpload}
          onRemove={(file) => {
            const updated = files
              .filter((f) => f.uid !== file.uid)
              .map((f) => f.url);
            onChange(updated);
          }}
          customRequest={async ({ file, onSuccess, onError }) => {
            try {
              const formData = new FormData();
              formData.append("image", file);
              const res = await api.upload_image(formData);
              const url = res.data.url;
              onChange([...value, url]); // parent-д шинэ зураг нэмэх
              onSuccess(res, file);
              toast("Зураг нэмэгдлээ", "success");
            } catch (err) {
              onError(err);
              toast("Алдаа гарлаа", "error");
            }
          }}
        >
          {files.length < 4 && "+ Зураг"}
        </Upload>
      </div>
    );
  };

  const handleSave = async () => {
    const payload = {
      id,
      title,
      content,
      image: {
        thumbnail,
        cover,
      },
    };
    const res = await api.update_news(payload);
    // console.log(res);
    if (res.status === 200) {
      toast("Амжилттай хадгалагдлаа", "success");
    } else {
      toast(
        res.data?.errors?.title ||
          res.data?.message ||
          "Хадгалах үед алдаа гарлаа",
        "error"
      );
    }
  };

  if (loading) return <Loader />;
  if (!news) return <div>Мэдээ олдсонгүй</div>;

  return (
    <Flex vertical gap="20px">
      <CustomHeader
        title="Мэдээний дэлгэрэнгүй"
        rightSide={
          news.status === "draft" ? (
            <Button
              variant="dashed"
              color="primary"
              onClick={() => setShowModal(true)}
              style={{ padding: "0 20px" }}
            >
              <IconPencilPlus size={16} /> Нийтлэх
            </Button>
          ) : (
            <Button
              variant="dashed"
              color="danger"
              onClick={() => setIsDraftModal(true)}
              style={{ padding: "0 20px" }}
            >
              <IconPencilMinus size={16} /> Ноорог болгох
            </Button>
          )
        }
      />
      <Flex gap="20px">
        <Flex vertical style={{ width: "100%" }} gap="10px">
          <UploadSection
            label="Thumbnail"
            value={thumbnail}
            onUpload={setThumbnail}
          />
          <UploadGallery value={cover} onChange={setCover} />
          <CustomInput
            required
            title="Гарчиг"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Flex vertical>
            <p style={{ fontSize: "13px" }}>
              <span style={{ color: "#f00" }}>*</span>Мэдээ:
            </p>
            <CustomEditor value={content} onChange={setContent} />
          </Flex>
        </Flex>
        <Flex
          vertical
          gap="10px"
          style={{
            border: "1px solid #d9d9d9",
            minWidth: "300px",
            padding: "20px",
            background: "#fafafa",
            borderRadius: "6px",
          }}
        >
          <DetailItem
            label="Төлөв"
            value={
              <span
                style={{
                  color: news.status === "draft" ? "default" : "green",
                }}
              >
                {capitalize(news.status === "draft" ? "ноорог" : "нийтлэгдсэн")}
              </span>
            }
          />
          <DetailItem
            label="Үүссэн огноо"
            value={formatDateTime(news.created_at)}
          />
          <DetailItem
            label="Нийтлэсэн огноо"
            value={news.published_at ? formatDateTime(news.published_at) : "-"}
          />
          <DetailItem
            label="Зохиогч"
            value={news.Author?.UserProfile?.name || "-"}
          />
        </Flex>
      </Flex>
      <Button type="primary" style={{ width: "200px" }} onClick={handleSave}>
        Хадгалах
      </Button>
      <ConfirmModal
        title="Мэдээг нийтлэх үү?"
        showModal={showModal}
        setShowModal={setShowModal}
        submitHandler={() => changeStatus("published")}
      />
      <ConfirmModal
        variant="warning"
        title="Мэдээг ноорог болгох уу?"
        showModal={isDraftModal}
        setShowModal={setIsDraftModal}
        submitHandler={() => changeStatus("draft")}
      />
    </Flex>
  );
};

const DetailItem = ({ label, value }) => (
  <Flex gap="10px" style={{ width: "100%", justifyContent: "space-between" }}>
    <p style={{ fontWeight: 500, fontSize: "13px", minWidth: 120 }}>{label}:</p>
    <p style={{ fontWeight: 600, fontSize: "13px", textAlign: "right" }}>
      {value}
    </p>
  </Flex>
);

export default NewsDetail;
